import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

// ─────────────────────────────────────────────────────────────────────────────
// Volet « Atelier » de la modale Demandes clients.
//
// Pendant que le call center ou le réceptionnaire a le client au téléphone, ce
// volet répond à « où en est mon véhicule ? » sans quitter la demande : le
// dossier vivant du planning (statut atelier coloré, mécanicien, OR,
// restitution prévue, position parking) et les passages précédents.
//
// Autonome : il interroge la recherche globale du planning
// (`GET /planning/interventions?search=`) — un utilisateur sans accès Planning
// reçoit 403 et le volet l'affiche sobrement, le serveur fait barrière.
// `onOuvrirDossier(intervention)` est fourni par l'environnement (le Planning
// ouvre sa modale d'intervention) ; sans prop, repli sur le deep-link
// sessionStorage + `crm-navigate` déjà utilisé par Gestion Parking.
// ─────────────────────────────────────────────────────────────────────────────

const normaliser = (v) => String(v || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

function DemandeVoletAtelier({ immatriculation, vin, onOuvrirDossier = null, version = 0 }) {
  const [etat, setEtat] = useState({ charge: false, erreur: null, droitRefuse: false, dossiers: [] });
  const [parking, setParking] = useState(null);
  const abortRef = useRef(null);

  const immatNorm = normaliser(immatriculation);
  const vinNorm = normaliser(vin);
  const cle = immatNorm.length >= 4 ? immatNorm : (vinNorm.length >= 8 ? vinNorm : '');

  useEffect(() => {
    if (!cle) { setEtat({ charge: false, erreur: null, droitRefuse: false, dossiers: [] }); setParking(null); return; }
    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setEtat(e => ({ ...e, charge: true, erreur: null }));
      try {
        const { data } = await api.get('/planning/interventions', {
          params: { search: cle, inclure_a_planifier: '1' },
          signal: ctrl.signal,
        });
        const brut = Array.isArray(data) ? data : (data.interventions || []);
        // La recherche est floue (ILIKE %…%) : ne garder que le véhicule EXACT.
        const dossiers = brut.filter(i =>
          normaliser(i.immat || i.immatriculation) === cle || normaliser(i.vin) === cle
        ).filter(i => !i.is_complement);
        setEtat({ charge: false, erreur: null, droitRefuse: false, dossiers });
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err.response?.status === 403) {
          setEtat({ charge: false, erreur: null, droitRefuse: true, dossiers: [] });
        } else {
          setEtat({ charge: false, erreur: 'Suivi atelier momentanément indisponible', droitRefuse: false, dossiers: [] });
        }
      }
    }, 400);
    return () => clearTimeout(t);
    // `version` : relecture après un enregistrement dans la modale du dossier —
    // le statut affiché ici doit suivre celui qu'on vient de changer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cle, version]);

  // Le dossier « vivant » : celui d'aujourd'hui, sinon le prochain à venir,
  // sinon aucun. Les autres forment l'historique (du plus récent au plus ancien).
  const aujourdHui = new Date();
  const ymdLocal = `${aujourdHui.getFullYear()}-${String(aujourdHui.getMonth() + 1).padStart(2, '0')}-${String(aujourdHui.getDate()).padStart(2, '0')}`;
  const dossiersTries = etat.dossiers.slice().sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
  const aVenir = dossiersTries.filter(d => String(d.date || '') >= ymdLocal);
  const vivant = aVenir.length ? aVenir[aVenir.length - 1] : null;   // le plus proche
  const passes = dossiersTries.filter(d => d !== vivant && String(d.date || '') < ymdLocal).slice(0, 5);

  // Position parking du dossier vivant (droits planning requis aussi : silencieux)
  useEffect(() => {
    setParking(null);
    if (!vivant?.id) return;
    let annule = false;
    api.get(`/planning/interventions/${vivant.id}/parking`)
      .then(r => { if (!annule && r.data?.position) setParking(r.data.position); })
      .catch(() => {});
    return () => { annule = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vivant?.id]);

  // Sur place quand l'écran appelant sait ouvrir la modale (Demandes clients) ;
  // bascule vers le Planning sinon — un écran qui n'a pas branché le dossier
  // doit rester utilisable.
  const surPlace = Boolean(onOuvrirDossier);

  const ouvrir = (intervention) => {
    if (onOuvrirDossier) { onOuvrirDossier(intervention); return; }
    // Sans branchement : bascule vers le Planning (patron Parking)
    try { sessionStorage.setItem('planning-open-intervention', String(intervention.id)); } catch (e) { /* stockage indisponible */ }
    window.dispatchEvent(new CustomEvent('crm-navigate', { detail: { page: 'planning' } }));
  };

  const fmtDate = (ymd) => {
    if (!ymd) return '—';
    if (ymd === ymdLocal) return "Aujourd'hui";
    const [a, m, j] = String(ymd).slice(0, 10).split('-');
    return `${j}/${m}${a !== String(aujourdHui.getFullYear()) ? '/' + a.slice(2) : ''}`;
  };

  if (!cle) return null;

  return (
    <aside className="leads-volet-atelier">
      <div className="lva-titre">
        🔧 Atelier
        <span className="lva-immat">{immatNorm || vinNorm}</span>
      </div>

      {etat.droitRefuse && <p className="lva-note">Suivi atelier — accès Planning requis.</p>}
      {etat.erreur && <p className="lva-note">⚠️ {etat.erreur}</p>}
      {etat.charge && etat.dossiers.length === 0 && <p className="lva-note">Recherche…</p>}
      {!etat.charge && !etat.erreur && !etat.droitRefuse && etat.dossiers.length === 0 && (
        <p className="lva-note">Aucun passage atelier connu pour ce véhicule.</p>
      )}

      {vivant && (
        <div className="lva-vivant">
          <div className="lva-vivant-entete">
            <span className="lva-vivant-date">
              {fmtDate(vivant.date)}{vivant.heure_reception ? ` · RDV à ${vivant.heure_reception}` : (vivant.heure_debut ? ` · ${vivant.heure_debut}` : '')}
            </span>
            {vivant.statut_atelier_nom ? (
              <span className="lva-chip" style={{ background: vivant.statut_atelier_couleur || 'var(--bg-muted)', color: vivant.statut_atelier_text_color || '#000' }}>
                {vivant.statut_atelier_nom}
              </span>
            ) : (
              <span className="lva-chip lva-chip--neutre">{vivant.a_planifier ? 'À planifier' : 'Planifié'}</span>
            )}
          </div>
          {vivant.mecanicien && <div className="lva-ligne"><span>Mécanicien</span><b>{vivant.mecanicien}</b></div>}
          {vivant.receptionnaire_nom && <div className="lva-ligne"><span>Réceptionnaire</span><b>{vivant.receptionnaire_nom}</b></div>}
          <div className="lva-ligne"><span>N° OR</span><b>{vivant.numero_or || 'à préparer'}</b></div>
          {vivant.heure_restitution_prevue && <div className="lva-ligne"><span>Restitution prévue</span><b>{vivant.heure_restitution_prevue}</b></div>}
          {vivant.restituee_at && <div className="lva-ligne"><span>Restitué</span><b>{new Date(vivant.restituee_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</b></div>}
          {parking?.zone && <div className="lva-ligne"><span>Parking</span><b>{parking.zone}</b></div>}
          {(vivant.a_courtoisie || vivant.client_attend || vivant.est_roole) && (
            <div className="lva-flags">
              {vivant.a_courtoisie && <span className="lva-mini">🚗 Courtoisie</span>}
              {vivant.client_attend && <span className="lva-mini">⏱ Client attend</span>}
              {vivant.est_roole && <span className="lva-mini">Roole</span>}
            </div>
          )}
          <button type="button" className="lva-ouvrir" onClick={() => ouvrir(vivant)}>
            {surPlace ? 'Ouvrir le dossier' : 'Ouvrir le dossier dans le Planning →'}
          </button>
        </div>
      )}

      {passes.length > 0 && (
        <>
          <div className="lva-histo-titre">Passages précédents</div>
          {passes.map(d => (
            <button type="button" key={d.id} className="lva-histo-ligne" onClick={() => ouvrir(d)} title={surPlace ? 'Ouvrir ce dossier' : 'Ouvrir ce dossier dans le Planning'}>
              <span className="lva-h-date">{fmtDate(d.date)}</span>
              <span className="lva-h-type">{d.description || d.type_intervention || '—'}</span>
              <span className="lva-h-statut" style={d.statut_atelier_couleur ? { background: d.statut_atelier_couleur, color: d.statut_atelier_text_color || '#000' } : undefined}>
                {d.restituee_at ? 'Restitué' : (d.statut_atelier_nom || '—')}
              </span>
            </button>
          ))}
        </>
      )}
    </aside>
  );
}

export default DemandeVoletAtelier;
